import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState, ACTION_TYPES } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import { GUIA_I_PREGUNTAS, GUIA_II_PREGUNTAS, OPCIONES_GUIA_II, GUIA_III_PREGUNTAS, OPCIONES_GUIA_III } from '../data';
import { Check } from 'lucide-react';

export default function AplicarCuestionarioPage() {
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();
  const t = getTheme(state.darkMode);
  const dark = state.darkMode;

  // Si el usuario sale de esta página sin guardar ni cancelar explícitamente
  // (ej. click directo en un link de la barra lateral), limpia el cuestionario
  // a medio contestar en vez de dejarlo "sucio" en el estado global — si no,
  // volver a esta ruta más tarde mostraría respuestas viejas de otro trabajador.
  useEffect(() => {
    return () => { dispatch({ type: ACTION_TYPES.CANCELAR_CUESTIONARIO }); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { trabajadorActual, guiaActual, respuestasActuales } = state;
  if (!trabajadorActual || !guiaActual) { navigate('/cuestionarios'); return null; }

  const setResp = (id, val) => dispatch({ type: ACTION_TYPES.SET_RESPUESTAS, payload: { ...respuestasActuales, [id]: val } });

  const cancelar = () => { dispatch({ type: ACTION_TYPES.CANCELAR_CUESTIONARIO }); navigate('/cuestionarios'); };

  const guardar = () => {
    dispatch({ type: ACTION_TYPES.GUARDAR_CUESTIONARIO, payload: { centroIdx: state.centroActualIdx, trabajadorId: trabajadorActual.id, guia: guiaActual, respuestas: respuestasActuales } });
    navigate('/cuestionarios');
  };

  // ── Styles ──────────────────────────────────────────────
  const viewStyle = { maxWidth: 720, margin: '0 auto', padding: '40px 24px' };
  const secTitulo = { fontSize: '0.9rem', fontWeight: 700, color: t.textLink, marginTop: 28, marginBottom: 12, padding: '10px 16px', background: t.accentLight, borderRadius: 8, borderLeft: `3px solid ${t.accent}` };
  const introStyle = { padding: '8px 14px', background: 'rgba(99,102,241,0.06)', borderRadius: 8, fontSize: '0.8rem', color: dark ? '#a5b4fc' : '#6366f1', marginBottom: 8, fontStyle: 'italic' };
  const card = { padding: 18, marginBottom: 10, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12 };
  const qText = { margin: '0 0 12px', fontSize: '0.92rem', lineHeight: 1.5, color: dark ? '#ddd' : '#374151' };
  const btnSiNo = (val, selected, isSi) => ({
    padding: '8px 24px', borderRadius: 8, fontSize: '0.88rem', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
    background: selected ? (isSi ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.08)') : t.bgInput,
    border: `1px solid ${selected ? (isSi ? '#ef4444' : '#10b981') : t.borderInput}`,
    color: selected ? (isSi ? '#ef4444' : '#10b981') : (dark ? '#aaa' : '#6b7280'),
  });
  const btnLikert = (selected) => ({
    padding: '7px 14px', borderRadius: 8, fontSize: '0.8rem', cursor: 'pointer', transition: 'all 0.15s',
    background: selected ? t.accentLight : t.bgInput,
    border: `1px solid ${selected ? t.accent : (dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb')}`,
    color: selected ? t.textLink : t.textMuted,
  });
  const progressBar = { height: 6, background: dark ? 'rgba(255,255,255,0.06)' : '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginBottom: 20 };
  const footerStyle = { display: 'flex', justifyContent: 'space-between', marginTop: 32, paddingTop: 20, borderTop: `1px solid ${t.border}` };
  const btnCancel = { padding: '12px 24px', background: 'none', border: `1px solid ${t.borderInput}`, borderRadius: 10, color: t.textMuted, fontSize: '0.9rem', cursor: 'pointer' };
  const btnSave = (enabled) => ({ padding: '12px 24px', background: t.accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, opacity: enabled ? 1 : 0.5 });

  // ── GUIA I ──────────────────────────────────────────────
  if (guiaActual === 'I') {
    const idsSecI = ['g1_1','g1_2','g1_3','g1_4','g1_5','g1_6'];
    const secIRespondida = idsSecI.every(id => respuestasActuales[id] !== undefined);
    const secIAlgunSi = idsSecI.some(id => respuestasActuales[id] === true);
    const secITodoNo = secIRespondida && !secIAlgunSi;
    const preguntasReq = secITodoNo ? idsSecI : GUIA_I_PREGUNTAS.filter(p => p.categoria).map(p => p.id);
    const respondidas = preguntasReq.filter(id => respuestasActuales[id] !== undefined).length;
    const total = preguntasReq.length;
    const completo = respondidas === total;

    return (
      <div style={viewStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>Guía de Referencia I</h2>
            <p style={{ color: t.textMuted, margin: '4px 0 0', fontSize: '0.88rem' }}>Acontecimientos traumáticos severos · {trabajadorActual.nombreCompleto || trabajadorActual.nombre}</p>
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: t.textLink }}>{respondidas}/{total}</div>
        </div>
        <div style={progressBar}><div style={{ height: '100%', width: `${Math.round(respondidas/total*100)}%`, background: t.accent, borderRadius: 3, transition: 'width 0.5s ease' }} /></div>

        <div style={{ marginTop: 28 }}>
          {GUIA_I_PREGUNTAS.map((item, idx) => {
            const esSecPosterior = item.categoria === 'recuerdos' || item.categoria === 'evitacion' || item.categoria === 'afectacion';
            const esTitSecPost = item.tipo === 'seccion' && (item.id === 2 || item.id === 3 || item.id === 4);
            if (secITodoNo && (esSecPosterior || esTitSecPost)) return null;
            if (!secIRespondida && (esSecPosterior || esTitSecPost)) return null;
            if (item.tipo === 'seccion') return <h3 key={idx} style={secTitulo}>{item.texto}</h3>;
            if (item.tipo === 'intro') return <p key={idx} style={introStyle}>{item.texto}</p>;
            return (
              <div key={item.id} style={card}>
                <p style={qText}>{item.texto}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[true, false].map(val => <button key={String(val)} style={btnSiNo(val, respuestasActuales[item.id] === val, val)} onClick={() => setResp(item.id, val)}>{val ? 'Sí' : 'No'}</button>)}
                </div>
              </div>
            );
          })}
        </div>

        {secITodoNo && <div style={{ padding: '12px 16px', background: 'rgba(16,185,129,0.08)', borderRadius: 10, marginBottom: 12, fontSize: '0.82rem', color: '#10b981', border: '1px solid rgba(16,185,129,0.2)' }}>✓ No se reportaron acontecimientos traumáticos. No es necesario responder las demás secciones.</div>}

        <div style={footerStyle}>
          <button style={btnCancel} onClick={cancelar}>Cancelar</button>
          <button style={btnSave(completo)} disabled={!completo} onClick={guardar}>Guardar respuestas <Check size={18} /></button>
        </div>
      </div>
    );
  }

  // ── GUIA II / III (Likert) ──────────────────────────────
  const isGIII = guiaActual === 'III';
  const PREGUNTAS = isGIII ? GUIA_III_PREGUNTAS : GUIA_II_PREGUNTAS;
  const OPCIONES = isGIII ? OPCIONES_GUIA_III : OPCIONES_GUIA_II;
  const titulo = isGIII ? 'Guía de Referencia III' : 'Guía de Referencia II';
  const subtitulo = isGIII ? 'Factores de riesgo psicosocial y entorno organizacional' : 'Factores de riesgo psicosocial';

  const allPreguntas = [];
  const filtroIds = [];
  PREGUNTAS.forEach(d => {
    if (d.preguntaFiltro) filtroIds.push(d.preguntaFiltro.id);
    d.preguntas.forEach(p => {
      if (p.condicional) { if (respuestasActuales[d.preguntaFiltro?.id] === true) allPreguntas.push(p); }
      else allPreguntas.push(p);
    });
  });
  const totalReq = allPreguntas.length + filtroIds.length;
  const respondidas = [...allPreguntas.map(p => p.id), ...filtroIds].filter(id => respuestasActuales[id] !== undefined).length;
  const completo = respondidas >= totalReq;

  return (
    <div style={viewStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>{titulo}</h2>
          <p style={{ color: t.textMuted, margin: '4px 0 0', fontSize: '0.88rem' }}>{subtitulo} · {trabajadorActual.nombreCompleto || trabajadorActual.nombre}</p>
        </div>
        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: t.textLink }}>{respondidas}/{totalReq}</div>
      </div>
      <div style={progressBar}><div style={{ height: '100%', width: `${totalReq > 0 ? Math.round(respondidas/totalReq*100) : 0}%`, background: t.accent, borderRadius: 3, transition: 'width 0.5s ease' }} /></div>

      <div style={{ marginTop: 28 }}>
        {PREGUNTAS.map((dominio, dIdx) => (
          <div key={dIdx}>
            {!dominio.visualContinue && <h3 style={secTitulo}>{dominio.dominio}</h3>}
            {dominio.intro && <p style={introStyle}>{dominio.intro}</p>}
            {dominio.preguntaFiltro && (
              <div style={card}>
                <p style={{ ...qText, fontWeight: 600 }}>{dominio.preguntaFiltro.texto}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[true, false].map(val => <button key={String(val)} style={btnSiNo(val, respuestasActuales[dominio.preguntaFiltro.id] === val, val)} onClick={() => setResp(dominio.preguntaFiltro.id, val)}>{val ? 'Sí' : 'No'}</button>)}
                </div>
              </div>
            )}
            {dominio.preguntas.map(p => {
              if (p.condicional && respuestasActuales[dominio.preguntaFiltro?.id] !== true) return null;
              return (
                <div key={p.id} style={card}>
                  <p style={qText}>{p.num && <span style={{ color: t.textLink, fontWeight: 700, marginRight: 6 }}>{p.num}.</span>}{p.texto}</p>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {OPCIONES.map(opt => <button key={opt.valor} style={btnLikert(respuestasActuales[p.id] === opt.valor)} onClick={() => setResp(p.id, opt.valor)}>{opt.texto}</button>)}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={footerStyle}>
        <button style={btnCancel} onClick={cancelar}>Cancelar</button>
        <button style={btnSave(completo)} disabled={!completo} onClick={guardar}>Guardar respuestas <Check size={18} /></button>
      </div>
    </div>
  );
}
